import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, FileText, CheckCircle, AlertCircle, X, Database, Calendar, Users } from 'lucide-react';
import Papa from 'papaparse';
import { useApp } from '../contexts/AppContext';

const ImportScreen = () => {
  const navigate = useNavigate();
  const { importCSVData, importedForms, setActiveForm, deleteForm, clearAllData } = useApp();
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [errors, setErrors] = useState([]);

  const processCSVFile = (file) => {
    return new Promise((resolve, reject) => {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          if (results.errors && results.errors.length > 0) {
            reject(new Error(`Error parsing CSV: ${results.errors[0].message}`));
          } else {
            resolve({
              headers: results.meta.fields,
              data: results.data
            });
          }
        },
        error: (error) => {
          reject(error);
        }
      });
    });
  };

  const handleDrop = useCallback(async (acceptedFiles) => {
    setProcessing(true);
    setErrors([]);
    
    const newUploads = [];

    for (const file of acceptedFiles) {
      try {
        const csvData = await processCSVFile(file);
        const form = importCSVData(file.name, csvData);
        
        setUploadedFiles(prev => [...prev, {
          id: form.id,
          name: file.name,
          size: file.size,
          status: 'success',
          contractorCount: form.contractorRows.length,
          eventCount: form.eventColumns.length
        }]);
      } catch (error) {
        console.error('Error processing file:', error);
        setUploadedFiles(prev => [...prev, {
          id: file.name,
          name: file.name,
          size: file.size,
          status: 'error',
          error: error.message
        }]);
        
        setErrors(prev => [...prev, {
          file: file.name,
          message: error.message
        }]);
      }
    }

    setProcessing(false);
  }, [importCSVData]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    accept: { 'text/csv': ['.csv'] }
  });

  const selectForm = (formId) => {
    setActiveForm(formId);
    navigate('/scheduler');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="glass rounded-lg p-6 border border-ops-border">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-ops-text-primary">Import Response Forms</h2>
            <p className="text-ops-text-secondary mt-1">Upload CSV files from your Google Forms responses</p>
          </div>
          <div className="flex items-center space-x-3 text-sm">
            <div className="flex items-center space-x-2">
              <Database className="w-4 h-4 text-ops-accent-primary" />
              <span className="text-ops-text-secondary">
                {importedForms.length} Forms Loaded
              </span>
            </div>
            {importedForms.length > 0 && (
              <button
                onClick={() => {
                  if (window.confirm('Clear all imported forms, events, contractors, and assignments?')) {
                    clearAllData();
                  }
                }}
                className="px-3 py-1.5 rounded-lg border border-ops-border text-ops-text-tertiary hover:border-ops-accent-danger hover:text-ops-accent-danger transition-colors text-xs uppercase tracking-wide"
              >
                Reset All
              </button>
            )}
          </div>
        </div>

        {/* Drop Zone */}
        <div
          {...getRootProps()}
          className={`
            relative border-2 border-dashed rounded-lg p-12 text-center cursor-pointer
            transition-all duration-300 hud-border
            ${isDragActive 
              ? 'border-ops-accent-primary bg-ops-bg-elevated/60' 
              : 'border-ops-border bg-ops-bg-elevated/40 hover:border-ops-accent-primary hover:bg-ops-bg-elevated/60'
            }
          `}
        >
          <input {...getInputProps()} />
          <div className="flex flex-col items-center justify-center space-y-3">
            <div className="w-12 h-12 rounded-full bg-ops-bg-card flex items-center justify-center mb-2">
              <Upload className="w-6 h-6 text-ops-accent-primary" />
            </div>
            <div>
              <p className="text-ops-text-primary font-medium">
                Drop CSV files here, or click to browse
              </p>
              <p className="text-ops-text-secondary text-sm mt-1">
                Export your Google Form responses as CSV and import them here.
              </p>
            </div>
            <div className="flex items-center space-x-2 text-xs text-ops-text-tertiary">
              <span className="px-2 py-1 rounded-full bg-ops-bg-card border border-ops-border">.csv only</span>
              <span className="px-2 py-1 rounded-full bg-ops-bg-card border border-ops-border">Header row required</span>
            </div>
          </div>

          {processing && (
            <div className="absolute inset-0 bg-ops-bg-primary/80 flex items-center justify-center rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="w-5 h-5 border-2 border-ops-accent-primary border-t-transparent rounded-full animate-spin" />
                <span className="text-ops-text-secondary text-sm">
                  Processing files…
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Upload Status */}
        <AnimatePresence>
          {uploadedFiles.length > 0 && (
            <motion.div
              className="mt-4 space-y-2"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 8 }}
            >
              {uploadedFiles.map(file => (
                <div
                  key={file.id}
                  className="flex items-center justify-between px-3 py-2 rounded-md bg-ops-bg-card border border-ops-border"
                >
                  <div className="flex items-center space-x-3">
                    <FileText className="w-4 h-4 text-ops-accent-primary" />
                    <div>
                      <p className="text-sm text-ops-text-primary">{file.name}</p>
                      <p className="text-xs text-ops-text-tertiary">
                        {(file.size / 1024).toFixed(1)} KB · {file.contractorCount || 0} contractors · {file.eventCount || 0} events
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {file.status === 'success' && (
                      <span className="inline-flex items-center text-xs text-green-400">
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Imported
                      </span>
                    )}
                    {file.status === 'error' && (
                      <span className="inline-flex items-center text-xs text-red-400">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        Error
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Error Messages */}
        {errors.length > 0 && (
          <div className="mt-4 rounded-md border border-red-500/50 bg-red-500/5 p-3">
            <div className="flex items-center mb-1">
              <AlertCircle className="w-4 h-4 text-red-400 mr-2" />
              <span className="text-sm font-medium text-red-200">Import issues detected</span>
            </div>
            <ul className="text-xs text-red-200 space-y-1">
              {errors.map((error, index) => (
                <li key={index}>
                  <span className="font-medium">{error.file}:</span> {error.message}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Imported Forms */}
      {importedForms.length > 0 && (
        <div className="glass rounded-lg p-6 border border-ops-border">
          <h3 className="text-lg font-semibold text-ops-text-primary mb-4">Available Forms</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {importedForms.map((form) => (
              <motion.div
                key={form.id}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => selectForm(form.id)}
                className="bg-ops-bg-card border border-ops-border rounded-lg p-4 cursor-pointer hover:border-ops-accent-primary transition-all"
              >
                <div className="flex items-start justify-between mb-3">
                  <FileText className="w-5 h-5 text-ops-accent-primary" />
                  <span className="text-xs text-ops-text-tertiary">
                    {new Date(form.uploadDate).toLocaleDateString()}
                  </span>
                </div>
                <h4 className="font-semibold text-ops-text-primary mb-2">{form.name}</h4>
                <div className="flex items-center space-x-4 text-sm text-ops-text-secondary">
                  <div className="flex items-center space-x-1">
                    <Users className="w-4 h-4" />
                    <span>{form.contractorRows.length}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Calendar className="w-4 h-4" />
                    <span>{form.eventColumns.length}</span>
                  </div>
                </div>
                <div className="mt-3 flex space-x-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      selectForm(form.id);
                    }}
                    className="flex-1 py-2 px-3 bg-ops-accent-primary text-ops-bg-primary rounded text-sm font-medium hover:bg-ops-accent-secondary transition-all"
                  >
                    Open Scheduler
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (window.confirm(`Remove form "${form.name}" and all associated events & assignments?`)) {
                        deleteForm(form.id);
                      }
                    }}
                    className="px-3 py-2 rounded border border-ops-accent-danger text-ops-accent-danger text-xs font-medium hover:bg-ops-accent-danger hover:text-ops-bg-primary transition-all"
                  >
                    Remove
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ImportScreen;
